import get from 'lodash/get';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import {stateLoadError} from '../../app/actions';
import {RouterStateError} from '../../app/enums/RouterStateError';
import {getRouter} from '../../config/reducers/state';
import {AbstractEpics} from '../redux/AbstractEpics';
import {isInView, onEnterView} from '../redux';
import {epicErrorHandler} from '../rx-utils';


/**
 * Exported for unit testing
 */
export class ReduxRouterStateEpics extends AbstractEpics {
  /**
   * @param {ReduxRouterStateOptions} options
   */
  constructor(options) {
    super(options);

    /** @type {ReduxRouterStateOptions} */
    this.options = options;
    /** @type {ReduxObservableItemActions} */
    this.reduxItemActions = options.reduxItem.actions;
    /** @type {ReduxObservableItemOptions} */
    this.reduxItemOptions = options.reduxItem.options;

    this.onEnterViewWatchParam = this.onEnterViewWatchParam.bind(this);
    this.addEpic(this.onEnterViewWatchParam);

    this.onLoadItemError = this.onLoadItemError.bind(this);
    this.addEpic(this.onLoadItemError);
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable.<ReduxAction>}
   */
  onEnterViewWatchParam(action$, store) {
    return onEnterView(action$, this.options.stateNames)
      .map(action => this.getIdParam(action.payload))
      .distinctUntilChanged()
      .switchMap(id => {
        const rootState = store.getState();
        const itemState = this.reduxItemOptions.getState(rootState);
        const routerState = getRouter(rootState);
        const existingItem = this.getExistingItem(routerState);
        if (itemState.init && itemState.id) {
          return Observable
            .merge(
              Observable.of(this.reduxItemActions.destroyItem({}, {id: itemState.id})),
              Observable.timer(33)
                .take(1)
                .mapTo(this.reduxItemActions.initItem({id, existingItem})),
            );
        } else {
          return Observable.of(this.reduxItemActions.initItem({id, existingItem}));
        }
      })
      .catch(epicErrorHandler(this.logger, 'ReduxRouterStateEpics.onEnterViewWatchParam'));
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable.<ReduxAction>}
   */
  onLoadItemError(action$, store) {
    return action$.ofType(this.reduxItemActions.loadItemError.TYPE, this.reduxItemActions.deleteItemSuccess.TYPE)
      .mergeMap(action => {
        const rootState = store.getState();
        const itemState = this.reduxItemOptions.getState(rootState);
        const routerState = getRouter(rootState);

        const actions = [];
        if (this.isInViewForId(routerState, action.meta.id)) {
          actions.push(stateLoadError({
            err: {
              code: RouterStateError.NO_DATA,
              messageId: this.options.noDataMessage,
              fallback: this.options.fallbackStateName,
            },
          }));
        }

        if (this.options.reduxItem.reducers.hasValidId(itemState, action)) {
          actions.push(this.reduxItemActions.destroyItem({}, action.meta));
        }

        if (actions.length > 0) {
          return Observable.from(actions);
        } else {
          return Observable.empty();
        }
      })
      .catch(epicErrorHandler(this.logger, 'ReduxRouterStateEpics.onLoadItemError'));
  }

  getIdParam(routerState) {
    return get(routerState, `currentParams.${this.options.idParam}`);
  }

  isInViewForId(routerState, id) {
    return isInView(routerState, this.options.stateNames) && this.getIdParam(routerState) === id;
  }

  getExistingItem(routerState) {
    return get(routerState, 'currentParams.existingItem');
  }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/redux-ui-router-utils/epics.js